'use client'

import React, { useState, useEffect, useRef } from 'react';
import useOtmsService from '../../hooks/useOtmsService';

const UserCardSimpleLine = ({ personId, className = '' }) => {
  const otmsServiceRef = useRef(null);
  const { personnel } = useOtmsService();
  const [userData, setUserData] = useState(null);
  const [positionData, setPositionData] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Store the service in a ref to avoid dependency updates
  useEffect(() => {
    otmsServiceRef.current = personnel;
  }, [personnel]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (!otmsServiceRef.current || !personId) return;
        
        setLoading(true);
        const [userResponse, positionsResponse] = await Promise.all([
          otmsServiceRef.current.getPersonById(personId),
          otmsServiceRef.current.getPrimaryPosition(personId).catch(() => null) // Catch error and return null
        ]);

        if (userResponse?.data) {
          setUserData({
            name: userResponse.data.fullName,
            imageUrl: userResponse.data.imageUrl,
          });
        }

        if (positionsResponse?.data) {
          setPositionData({
            position: positionsResponse.data.position.title,
          });
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      } finally {
        setLoading(false);
      }
    };

    if (personId) {
      fetchData();
    }
  }, [personId]); // Remove personnel from dependencies

  if (loading) {
    return (
      <div className="flex items-center">
        <div className="animate-pulse flex items-center space-x-2">
          <div className="rounded-full bg-gray-200 h-5 w-5"></div>
          <div className="h-3 bg-gray-200 rounded w-24"></div>
        </div>
      </div>
    );
  }

  if (!userData) return null;

  return (
    <div className={`flex items-center space-x-1.5 ${className}`}>
      {userData.imageUrl ? (
        <img
          src={userData.imageUrl}
          alt={userData.name}
          className="h-5 w-5 rounded-full"
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = `data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%23666"><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" font-size="24">${userData.name?.charAt(0) || '?'}</text></svg>`;
          }}
        />
      ) : (
        <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-gray-500">
          <span className="text-xs font-medium leading-none text-white">
            {userData.name?.charAt(0) || '?'}
          </span>
        </span>
      )}
      <span className="text-sm font-medium text-gray-900">{userData.name}</span>
      {positionData?.position && (
        <span className="text-xs text-gray-500 truncate whitespace-nowrap">({positionData.position})</span>
      )}
    </div>
  );
};

export default UserCardSimpleLine; 